import axios, { type InternalAxiosRequestConfig } from 'axios';
import getEnv from '@shared/utils/env';

const CSRF_HEADER = 'x-csrf-token';
const CSRF_REFUSAL = 'CSRF proof is missing or invalid';
const SAFE_METHODS = ['get', 'head', 'options'];

const axiosInstance = axios.create({
  baseURL: getEnv('VUE_APP_API_URL'),
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  validateStatus: function (status: number) {
    return status >= 200 && status < 300;
  },
});

// Every API response carries a CSRF proof in a header, because the portal's host cannot read the
// API's cookies. Mutations send back the latest one.
let csrfProof: string | undefined;
let pendingProof: Promise<unknown> | null = null;

function remember(headers: unknown) {
  const value = (headers as Record<string, unknown> | undefined)?.[CSRF_HEADER];
  if (typeof value === 'string' && value !== '') {
    csrfProof = value;
  }
}

async function currentProof(): Promise<string | undefined> {
  if (!csrfProof) {
    // Any response carries one, including the 401 for a visitor who is not signed in.
    pendingProof ??= axiosInstance
      .get('/sessions/current')
      .catch(() => undefined)
      .finally(() => {
        pendingProof = null;
      });
    await pendingProof;
  }
  return csrfProof ?? legacyCookieProof();
}

// TEMPORARY: backends up to release 1.1.1 send no proof header and set this readable cookie
// instead. Revert the commit that added this once production runs a later backend.
const LEGACY_PROOF_COOKIE = 'fransys_csrf_proof=';

function legacyCookieProof(): string | undefined {
  return document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(LEGACY_PROOF_COOKIE))
    ?.slice(LEGACY_PROOF_COOKIE.length);
}

axiosInstance.interceptors.request.use(async (config) => {
  if (!SAFE_METHODS.includes((config.method ?? 'get').toLowerCase())) {
    const proof = await currentProof();
    if (proof) {
      config.headers.set(CSRF_HEADER, proof);
    }
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    remember(response.headers);
    return response;
  },
  (error) => {
    remember(error.response?.headers);
    const config = error.config as
      | (InternalAxiosRequestConfig & { csrfRetried?: boolean })
      | undefined;
    // A session change in another tab makes the proof stale; the refusal carries a fresh one.
    if (
      config &&
      !config.csrfRetried &&
      error.response?.status === 403 &&
      error.response.data?.errors?.[0]?.detail === CSRF_REFUSAL
    ) {
      config.csrfRetried = true;
      return axiosInstance(config);
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
